/**
 * Title-menu captain-name persistence — pure helpers (bug fix: "the name won't save").
 *
 * The menu used to seed the name field with a throwaway random "Captain####" every visit and never
 * remembered what the captain typed, so a returning captain always saw a random name and — if they just
 * tapped SET SAIL — sailed as that random Captain#### (looked like "my name didn't save"). These pure,
 * side-effect-free helpers hold the decisions; main.ts wires them to the DOM + localStorage (`sor:name`).
 * Kept pure so the round-trip is unit-testable in the repo's node (no-DOM) test environment.
 */

/** Pre-fill value for the menu name field: the captain's saved name if they have one (so a returning
 *  captain sees it, never retyping), else a fresh random "Captain####" default for a brand-new device. */
export function seedMenuName(savedName: string | null | undefined, rnd: () => number = Math.random): string {
  const s = (savedName ?? '').trim();
  return s || `Captain${Math.floor(rnd() * 900 + 100)}`;
}

/** What the client sends to the server on SET SAIL. Only a name the captain actually CHOSE is sent — so it
 *  wins and PERSISTS as a rename — while an untouched random default is sent as '' so the server keeps the
 *  captain's existing profile name (a returning captain is never clobbered by a throwaway "Captain####"). */
export function nameToSend(nameChosen: boolean, typed: string): string {
  return nameChosen ? typed.trim() : '';
}

/** Whether SET SAIL should persist `typed` as the captain's saved name (only a real, chosen name is cached). */
export function shouldPersistName(nameChosen: boolean, typed: string): boolean {
  return nameChosen && typed.trim().length > 0;
}
